import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export type AssignTargetStudent = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  class?: string | null;
  current_semester?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  students: AssignTargetStudent[];
  onDone?: () => void;
};

const SCHOOL_FEE_KIND = "school_fee";
const OTHER_KIND = "other";
const LEGACY_STRUCTURE_KIND = "structure";

export async function applyFeeStructure(
  student: AssignTargetStudent,
  categoryId: string,
  opts: { replaceExisting: boolean },
): Promise<{ created: number; removed: number; skippedPaid: number; skippedExisting: number }> {
  const { data: cat, error: ce } = await supabase
    .from("fee_categories")
    .select("id, name, fee_structure_items(id, amount, semester, cost_type_id, fee_cost_types(code, name)), fee_installments(id, amount, semester, installment_no, label, kind)")
    .eq("id", categoryId)
    .single();
  if (ce) throw ce;
  if (!cat) throw new Error("Fee structure not found");

  const structureItems = (cat.fee_structure_items ?? []) as any[];
  const feeInstallments = ((cat as any).fee_installments ?? []) as any[];
  const selectedCostTypeIds = new Set(structureItems.map((it: any) => it.cost_type_id).filter(Boolean));

  let removed = 0;
  let skippedPaid = 0;
  let skippedExisting = 0;
  if (opts.replaceExisting) {
    const { data: existing, error: ee } = await supabase
      .from("invoices")
      .select("id, status, kind, cost_type_id")
      .eq("student_id", student.id)
      .in("kind", [SCHOOL_FEE_KIND, OTHER_KIND, LEGACY_STRUCTURE_KIND]);
    if (ee) throw ee;
    const toDelete: string[] = [];
    for (const inv of existing ?? []) {
      const isStructureInvoice = inv.kind === SCHOOL_FEE_KIND || inv.kind === LEGACY_STRUCTURE_KIND || (inv.kind === OTHER_KIND && !!inv.cost_type_id);
      const matchesNewStructure = !inv.cost_type_id || selectedCostTypeIds.has(inv.cost_type_id);
      if (!isStructureInvoice || !matchesNewStructure) continue;
      if (inv.status === "unpaid") toDelete.push(inv.id);
      else skippedPaid++;
    }
    if (toDelete.length) {
      const { error: de } = await supabase.from("invoices").delete().in("id", toDelete);
      if (de) throw de;
      removed = toDelete.length;
    }
  }

  const feeAdmissionTotal = feeInstallments
    .filter((it: any) => (it.kind ?? "fee_admission") === "fee_admission")
    .reduce((sum: number, it: any) => sum + (Number(it.amount) || 0), 0)
    || structureItems
      .filter((it: any) => it.fee_cost_types?.code === "FEE_ADMISSION")
      .reduce((sum: number, it: any) => sum + (Number(it.amount) || 0), 0);

  const rows = [
    ...(feeAdmissionTotal > 0 ? [{
      student_id: student.id,
      amount: feeAdmissionTotal,
      description: `${cat.name} — Fee & Admission`,
      status: "unpaid",
      kind: SCHOOL_FEE_KIND,
      cost_type_id: null,
    }] : []),
    ...structureItems
      .filter((it: any) => it.fee_cost_types?.code !== "FEE_ADMISSION" && (Number(it.amount) || 0) > 0)
      .map((it: any) => ({
        student_id: student.id,
        amount: Number(it.amount) || 0,
        description: `${cat.name} — ${it.fee_cost_types?.name ?? "Other"} (Sem ${it.semester})`,
        status: "unpaid",
        kind: OTHER_KIND,
        cost_type_id: it.cost_type_id,
      })),
  ];

  const rowsToInsert = opts.replaceExisting ? rows : [];
  if (!opts.replaceExisting && rows.length) {
    const { data: existing, error: ee } = await supabase
      .from("invoices")
      .select("kind, cost_type_id")
      .eq("student_id", student.id)
      .in("kind", [SCHOOL_FEE_KIND, OTHER_KIND]);
    if (ee) throw ee;
    for (const row of rows) {
      const alreadyExists = (existing ?? []).some((inv: any) =>
        inv.kind === row.kind && (row.kind === SCHOOL_FEE_KIND || inv.cost_type_id === row.cost_type_id)
      );
      if (alreadyExists) skippedExisting++;
      else rowsToInsert.push(row);
    }
  }

  let created = 0;
  if (rowsToInsert.length) {
    const { error: ie } = await supabase.from("invoices").insert(rowsToInsert);
    if (ie) throw ie;
    created = rowsToInsert.length;
  }
  const { error: profileErr } = await (supabase.from("student_accounting_profile") as any)
    .upsert({ student_id: student.id, fee_category_id: categoryId }, { onConflict: "student_id" });
  if (profileErr) throw profileErr;
  return { created, removed, skippedPaid, skippedExisting };
}

export function AssignFeeStructureDialog({ open, onOpenChange, students, onDone }: Props) {
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState<string>("");
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [running, setRunning] = useState(false);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["fee_categories_assign"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fee_categories")
        .select("id, name, classes, academic_year, student_type")
        .order("academic_year", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Default category: one whose classes contain at least one selected student's class
  useEffect(() => {
    if (!open) return;
    if (categoryId) return;
    if (!categories.length) return;
    const studentClasses = new Set(students.map(s => s.class).filter(Boolean) as string[]);
    const match = categories.find((c: any) => (c.classes ?? []).some((cls: string) => studentClasses.has(cls)));
    if (match) setCategoryId(match.id);
  }, [open, categories, students, categoryId]);

  useEffect(() => { if (!open) { setCategoryId(""); setReplaceExisting(true); } }, [open]);

  const distinctClasses = useMemo(
    () => Array.from(new Set(students.map(s => s.class).filter(Boolean))) as string[],
    [students],
  );

  const handleApply = async () => {
    if (!categoryId) { toast({ title: "Pick a fee structure", variant: "destructive" }); return; }
    if (!students.length) return;
    setRunning(true);
    let ok = 0, fail = 0, created = 0, removed = 0, skippedPaid = 0, skippedExisting = 0;
    let lastError = "";
    for (const s of students) {
      try {
        const r = await applyFeeStructure(s, categoryId, { replaceExisting });
        ok++;
        created += r.created;
        removed += r.removed;
        skippedPaid += r.skippedPaid;
        skippedExisting += r.skippedExisting;
      } catch (e: any) {
        fail++;
        lastError = e?.message || String(e);
        console.error("[applyFeeStructure] failed for", s.id, e);
      }
    }
    setRunning(false);
    qc.invalidateQueries({ queryKey: ["accounting-student-roster"] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    onDone?.();
    toast({
      title: `Fee structure applied to ${ok}/${students.length} student${students.length === 1 ? "" : "s"}`,
      description: `${created} invoice${created === 1 ? "" : "s"} created, ${removed} replaced${skippedPaid ? `, ${skippedPaid} paid kept` : ""}${skippedExisting ? `, ${skippedExisting} existing kept` : ""}${fail ? `, ${fail} failed${lastError ? ` — ${lastError}` : ""}` : ""}.`,
      variant: fail ? "destructive" : "default",
    });
    if (!fail) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !running && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign / Change Fee Structure</DialogTitle>
          <DialogDescription>
            {students.length === 1
              ? `${students[0].first_name ?? ""} ${students[0].last_name ?? ""} — ${students[0].class ?? "—"}`
              : `${students.length} students selected`}
            {distinctClasses.length > 0 && (
              <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                {distinctClasses.map(c => <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>)}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-sm">Fee Structure</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={isLoading ? "Loading…" : "Pick a pre-created fee structure"} />
              </SelectTrigger>
              <SelectContent position="popper">
                {categories.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {c.academic_year} · {(c.classes ?? []).join(", ") || "—"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox checked={replaceExisting} onCheckedChange={(v) => setReplaceExisting(!!v)} />
            <span>
              <span className="font-medium">Replace existing fee invoices</span>
              <span className="block text-xs text-muted-foreground">
                Deletes unpaid Fee &amp; Admission and matching other-cost invoices before inserting the new ones. Paid invoices are kept.
              </span>
            </span>
          </label>

          <p className="text-xs text-muted-foreground">
            Applying with replacement recreates Fee &amp; Admission plus all non-zero other costs from the picked structure.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running}>Cancel</Button>
          <Button onClick={handleApply} disabled={running || !categoryId}>
            {running && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
